<template>
  <div class="rm rm-theme ss ss--phone">
    <!-- početni ekran: pretraga i grupe -->
    <template v-if="!ctl.currentKey">
      <div class="rm-home-header">
        <span v-if="restaurantName" class="rm-muted">{{ restaurantName }}</span>
        <h1 class="rm-home-header__title">{{ $t("settings") }}</h1>
      </div>
      <div class="ss-ph-search">
        <label class="ss-srch">
          <v-icon size="18">mdi-magnify</v-icon>
          <span class="rm-visually-hidden">{{ $t("settings_shell.search") }}</span>
          <input
            type="search"
            autocomplete="off"
            :value="ctl.ui.query"
            :placeholder="$t('settings_shell.search')"
            @input="ctl.ui.query = $event.target.value"
          />
        </label>
      </div>

      <div v-if="query" class="ss-ph-res" role="listbox">
        <div v-if="!ctl.searchResults.length" class="ss-sres__none">{{ $t("settings_shell.search_none", { q: query }) }}</div>
        <button
          v-for="(hit, i) in ctl.searchResults"
          :key="`${hit.screen}-${hit.id}-${i}`"
          type="button"
          class="ui-sr"
          role="option"
          @click="ctl.goTo(hit.screen, hit.id)"
        >
          <span class="ss-sr__i"><v-icon size="16">{{ icon(hit.screen) }}</v-icon></span>
          <span class="ss-sr__t">
            <b>{{ hit.text }}</b>
            <em>{{ hit.screenTitle }}</em>
          </span>
        </button>
      </div>

      <div v-else class="rm-home-groups">
        <section v-for="group in ctl.groups" :key="group.key" class="rm-home-group">
          <h2 class="rm-section-title">{{ group.title }}</h2>
          <div class="rm-card">
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rm-menu-row"
              :data-key="item.key"
              @click="ctl.openScreen(item.key)"
            >
              <span class="rm-menu-row__icon"><v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon></span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
                <span class="ss-mrow__s">{{ ctl.summaryFor(item.key) }}</span>
              </span>
              <span v-if="item.badge" class="ss-mrow__d" :title="$t('settings_shell.status.unsaved', { n: item.badge })">{{ item.badge }}</span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- pojedinačni ekran -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button type="button" class="rm-icon-btn" :aria-label="$t('report_mobile.back')" @click="back">
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text">
            <h1 class="rm-detail-header__title">{{ ctl.screenTitle(ctl.currentKey) }}</h1>
            <span class="rm-muted rm-detail-header__sub">{{ ctl.screenSub(ctl.currentKey) }}</span>
          </div>
        </div>
      </div>
      <set-banner />
      <div class="ss-ph-b" :class="{ 'has-bar': showBar }">
        <settings-screen :screen="ctl.currentKey" />
      </div>
    </template>

    <!-- donja traka dok ima izmjena -->
    <div v-if="showBar" class="ss-pbar">
      <button type="button" class="ss-pbar__s" data-open="changes" :disabled="ctl.ui.saving" @click="ctl.ui.pop = 'changes'">
        <i class="ss-dot"></i>
        <b>{{ $t("settings_shell.status.unsaved", { n: ctl.changeCount }) }}</b>
        <span v-if="ctl.errorCount" class="ss-pbar__e">{{ $t("settings_shell.status.errors", { n: ctl.errorCount }) }}</span>
        <u v-else>{{ $t("settings_shell.review") }}</u>
      </button>
      <button type="button" class="ui-btn" :disabled="ctl.ui.saving" @click="ctl.discard()">{{ $t("settings_shell.discard") }}</button>
      <button type="button" class="ui-btn ui-btn--primary" :disabled="ctl.ui.saving" @click="ctl.save()">
        <i v-if="ctl.ui.saving" class="ss-spin ss-spin--w"></i>
        {{ ctl.ui.saving ? $t("settings_shell.saving") : $t("settings_shell.save") }}
      </button>
    </div>

    <template v-if="sheetKind">
      <div class="ss-scrim" @click="ctl.ui.pop = null"></div>
      <set-panel :key="sheetKind" sheet :kind="sheetKind" />
    </template>
    <set-toast />
    <set-dialog />
  </div>
</template>

<script>
import "@/components/report/mobile/reportMobileShell.scss";
import "../settingsShell.scss";
import { screenByKey } from "../settingsRegistry";
import SettingsScreen from "../SettingsScreen.vue";
import SetBanner from "../parts/SetBanner.vue";
import SetPanel from "../parts/SetPanel.vue";
import SetToast from "../parts/SetToast.vue";
import SetDialog from "../parts/SetDialog.vue";

const SHEETS = ["changes", "quick", "cycle", "uniform"];

/**
 * Podešavanja restorana na telefonu: početni ekran sa pretragom i grupama, ekran sa zaglavljem, donja traka
 * (izmjene, Odbaci, Snimi) i donje kartice za pregled izmjena i brzo podešavanje rokova.
 */
export default {
  name: "SettingsMobile",
  components: { SettingsScreen, SetBanner, SetPanel, SetToast, SetDialog },
  inject: { ctl: "settingsCtl" },
  data: () => ({ pushed: false }),
  computed: {
    query() {
      return this.ctl.ui.query.trim();
    },
    restaurantName() {
      return this.toLatin((this.ctl.restaurant && this.ctl.restaurant.name) || "");
    },
    showBar() {
      return Boolean(this.ctl.changeCount || this.ctl.ui.saving);
    },
    sheetKind() {
      return SHEETS.includes(this.ctl.ui.pop) ? this.ctl.ui.pop : null;
    },
  },
  watch: {
    "ctl.currentKey"(key, previous) {
      if (key && !previous) {
        this.pushed = true;
      }
      this.ctl.ui.pop = null;
      window.scrollTo(0, 0);
    },
  },
  methods: {
    icon(screen) {
      return (screenByKey(screen) || {}).icon || "mdi-cog-outline";
    },
    /** Ekran otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad"); direktan link → zamjena adrese. */
    back() {
      if (this.pushed) {
        this.pushed = false;
        this.$router.back();
        return;
      }
      this.ctl.closeScreen();
    },
  },
};
</script>
